import {useState} from 'react';
import {useSelector} from 'react-redux';

import {resetAutoDeliveryDetailsDcx} from '@/api/consumerApi';
import {AppButton} from '@/components/Button';
import AppLoader from '@/components/Loader/AppLoader/AppLoader';
import amplitudeAutoDeliveryDcxTracking from '@/services/analytics/amplitude/amplitudeAutoDeliveryDcxTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import appContent from '@/services/content/appContent.typed';
import iccAutoDeliveryDcxService from '@/services/icc/iccAutoDeliveryDcxService';
import routerService from '@/services/route/routerService';
import {selectLanguageCode} from '@/state/selectors/global';
import type {SkipAutoDeliveryDcxResponse} from '@/types/icc/autoDeliveryDetails';
import dateTimeUtils from '@/utils/dateTimeUtils';
import {normalizeApiDate} from '@/utils/normalizeApiDate';
import stringUtils from '@/utils/stringUtils';

import AutoDeliveryResultPopup from '../components/AutoDeliveryResultPopup';
import useOrderSummaryTracking from '../hooks/useOrderSummaryTracking';

const ConfirmSkipButton = () => {
    const languageCode = useSelector(selectLanguageCode);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [nextDeliveryDate, setNextDeliveryDate] = useState<string | null>(null);
    const orderSummaryTrackingValue = useOrderSummaryTracking();

    const skipDeliveryContent = appContent.getAutoDeliveryContent()?.skipDeliveryContent;

    const handleConfirmSubmit = () => {
        amplitudeAutoDeliveryDcxTracking.trackAutoDeliveryDcxSkipDeliveryStarted(orderSummaryTrackingValue);
        googleAnalyticsService.trackAutoDeliverySkipDeliveryStarted(orderSummaryTrackingValue);
        setIsLoading(true);
        iccAutoDeliveryDcxService.skipAutoDeliveryDcx({
            onSuccess: (responseData: SkipAutoDeliveryDcxResponse) => {
                setIsLoading(false);
                setNextDeliveryDate(responseData?.AutoDelivery?.NextDeliveryDate ?? null);
                setIsSuccessPopupVisible(true);
                // TODO: setDeliverySkipped is disabled — unclear when/where this flag should be reset.
                // Currently it doesn't persist across page reloads (stored in Redux only).
                // dispatch(setDeliverySkipped(true));
                amplitudeAutoDeliveryDcxTracking.trackAutoDeliveryDcxSkipDeliveryCompleted({
                    success: true,
                    message: undefined,
                    orderSummary: orderSummaryTrackingValue,
                });
                googleAnalyticsService.trackAutoDeliverySkipDeliveryCompleted({
                    success: true,
                    message: undefined,
                    orderSummary: orderSummaryTrackingValue,
                });
            },
            onError: (errorMessage: string) => {
                setIsLoading(false);
                setIsErrorPopupVisible(true);
                amplitudeAutoDeliveryDcxTracking.trackAutoDeliveryDcxSkipDeliveryCompleted({
                    success: false,
                    message: errorMessage,
                    orderSummary: orderSummaryTrackingValue,
                });
                googleAnalyticsService.trackAutoDeliverySkipDeliveryCompleted({
                    success: false,
                    message: errorMessage,
                    orderSummary: orderSummaryTrackingValue,
                });
            },
        });
    };

    const handleResultPopupClose = () => {
        setIsErrorPopupVisible(false);
        setIsSuccessPopupVisible(false);
    };

    const handleSuccessButtonClick = () => {
        resetAutoDeliveryDetailsDcx();
        routerService.goBack();
        handleResultPopupClose();
    };

    const handleErrorButtonClick = () => {
        handleResultPopupClose();
    };

    if (!skipDeliveryContent) return null;

    const {confirmButton, successPopup, failurePopup, dateDayPluralForms} = skipDeliveryContent;

    const getSuccessDescription = (): string | undefined => {
        if (!successPopup?.description || !nextDeliveryDate) return undefined;

        const date = normalizeApiDate(nextDeliveryDate);

        if (!date) return undefined;

        const ordinalDay = dateTimeUtils.getOrdinalDay(date.getDate(), dateDayPluralForms, languageCode);
        const month = dateTimeUtils.getMonth(date, languageCode);

        return stringUtils.formatString(successPopup.description, ordinalDay, month);
    };

    return (
        <>
            <AppButton
                primary
                light
                className='mt-0 w-full'
                onClick={handleConfirmSubmit}
                aria-label={confirmButton?.alt}
            >
                {confirmButton?.text}
            </AppButton>
            {/* @ts-expect-error AppLoader is not typed */}
            {isLoading && <AppLoader />}
            {isSuccessPopupVisible && successPopup && (
                <AutoDeliveryResultPopup
                    popup={successPopup}
                    description={getSuccessDescription()}
                    onClose={handleSuccessButtonClick}
                    onButtonClick={handleSuccessButtonClick}
                />
            )}
            {isErrorPopupVisible && failurePopup && (
                <AutoDeliveryResultPopup
                    popup={failurePopup}
                    onClose={handleErrorButtonClick}
                    onButtonClick={handleErrorButtonClick}
                />
            )}
        </>
    );
};

export default ConfirmSkipButton;
